import StoreInterfaceClass from "../../infrastructure/store-interface";
import IndexedDbInterfaceClass from "../../infrastructure/indexed-db-interface";

// Define allowed storage strategies
export type StoreType = "store" | "indexed_db";

export default class StoreUtilClass {
	private indexed_db_interface: IndexedDbInterfaceClass | null;
	private store_interface: StoreInterfaceClass;
	private store_entity_name: string;
	private used_cache_type_entity_name: string;
	private index_prop: string;
	private store_items_limit: number | null;
	private is_permanent: boolean | null;

	constructor(
		entity_name: string = "",
		store_items_limit: number | null = null,
		index_prop: string = "id",
		is_permanent: boolean | null = null
	) {
		this.indexed_db_interface = store_items_limit
			? new IndexedDbInterfaceClass(entity_name)
			: null;

		this.store_interface = new StoreInterfaceClass();
		this.store_entity_name = entity_name;
		this.used_cache_type_entity_name = `${entity_name}_cache_type`;
		this.index_prop = index_prop;

		this.store_items_limit = store_items_limit;
		this.is_permanent = is_permanent;
	}

	/**
	 * Stores static payload into standard store storage.
	 */
	public set_data(data: any, expiration_date: Date | null = null): void {
		this._set_used_store_type("store");
		this.store_interface.set(
			this.store_entity_name,
			data,
			expiration_date,
			this.is_permanent
		);
	}

	/**
	 * Retrieves generic data stored under current entity name.
	 */
	public get_data<T = any>(): T | null {
		const stored_type = this._get_used_store_type();
		if (!stored_type) return null;

		const result = this.store_interface.get<T>(this.store_entity_name);
		return result ?? null;
	}

	/**
	 * Saves array items automatically choosing between Store or IndexedDB
	 * based on configured item limits.
	 */
	public async save_items(
		data: any[],
		expiration_date: Date | null = null
	): Promise<void> {
		if (
			this.store_items_limit &&
			data.length >= this.store_items_limit &&
			this.indexed_db_interface
		) {
			this._set_used_store_type("indexed_db");
			await this.indexed_db_interface.set(
				data,
				this.index_prop,
				expiration_date
			);
		} else {
			this._set_used_store_type("store");
			this.store_interface.set(
				this.store_entity_name,
				data,
				expiration_date,
				this.is_permanent
			);
		}
	}

	/**
	 * Fetches collection items from the active storage mechanism.
	 */
	public async get_items<T = any>(): Promise<T | null> {
		const stored_type = this._get_used_store_type();
		if (!stored_type) return null;

		let result: T | null = null;
		switch (stored_type) {
			case "store":
				result = this.store_interface.get<T>(this.store_entity_name) ?? null;
				break;
			case "indexed_db":
				if (this.indexed_db_interface) {
					result = (await this.indexed_db_interface.get()) as T | null;
				}
				break;
		}
		return result;
	}

	/**
	 * Queries a single cached item matching the specified primary key ID.
	 */
	public async get_cached_item_by_id<T = any>(
		item_id: string | number
	): Promise<T | null> {
		const stored_type = this._get_used_store_type();
		if (!stored_type) return null;

		let result: T | null = null;
		switch (stored_type) {
			case "store":
				result = this.store_interface.find_by_property(
					this.store_entity_name,
					this.index_prop,
					item_id
				);
				break;
			case "indexed_db":
				if (this.indexed_db_interface) {
					result = (await this.indexed_db_interface.get(item_id)) as T | null;
				}
				break;
		}

		return result;
	}

	/**
	 * Mutates or creates an entry item inside active store or IndexedDB driver.
	 */
	public async add_or_update_item(item: Record<string, any>): Promise<void> {
		let stored_type = this._get_used_store_type();
		if (!stored_type) {
			this._set_used_store_type("store");
			stored_type = "store";
		}

		switch (stored_type) {
			case "store":
				this.store_interface.update_or_create_by_property(
					this.store_entity_name,
					this.index_prop,
					item[this.index_prop] ?? item.id,
					item
				);
				break;
			case "indexed_db":
				if (this.indexed_db_interface) {
					await this.indexed_db_interface.put(
						item,
						item[this.index_prop] ?? item.id
					);
				}
				break;
		}
	}

	/**
	 * Deletes a specific item matching the given identifier.
	 */
	public async remove_item(item_id: string | number): Promise<void> {
		const stored_type = this._get_used_store_type();
		if (!stored_type) return;

		switch (stored_type) {
			case "store":
				this.store_interface.delete_by_property(
					this.store_entity_name,
					this.index_prop,
					item_id
				);
				break;
			case "indexed_db":
				if (this.indexed_db_interface) {
					await this.indexed_db_interface.delete(item_id);
				}
				break;
		}
	}

	/**
	 * Purges all collection entries associated with this entity.
	 */
	public async remove_all_items(): Promise<void> {
		const stored_type = this._get_used_store_type();
		if (!stored_type) return;

		switch (stored_type) {
			case "store":
				this.store_interface.delete_entity_values(this.store_entity_name);
				break;
			case "indexed_db":
				if (this.indexed_db_interface) {
					await this.indexed_db_interface.clear_entity();
				}
				break;
		}
	}

	/**
	 * Internal flag setter tracking current active strategy ("store" | "indexed_db").
	 */
	private _set_used_store_type(store_type: StoreType): void {
		this.store_interface.set(
			this.used_cache_type_entity_name,
			store_type,
			null,
			this.is_permanent
		);
	}

	/**
	 * Internal flag getter evaluating current active strategy.
	 */
	private _get_used_store_type(): StoreType | null {
		return this.store_interface.get(
			this.used_cache_type_entity_name
		) as StoreType | null;
	}
}
